import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import {
  trackSsoCallbackError,
  trackSsoCodeExchangeError,
  extractAzureErrorCode,
} from "@/lib/ssoErrorTracker";
import {
  isCodeAlreadyProcessed,
  markCodeAsProcessed,
  resetProcessedCodes,
} from "@/lib/oauthCodeDedup";
import {
  AlertCircle,
  Loader2,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Settings,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AuthBrandMark } from "@/components/auth/AuthBrandMark";

type CallbackState = "loading" | "error" | "success";

interface CallbackError {
  title: string;
  description: string;
  details?: string;
  errorCode?: string;
  isAdminError?: boolean;
  domain?: string;
  providerId?: string;
}

/**
 * Known SSO/Supabase error patterns mapped to user-friendly messages
 */
interface ErrorMapping {
  pattern: RegExp;
  titleKey: string;
  descriptionKey: string;
  isAdminError: boolean;
  extractDomain?: boolean;
  extractProviderId?: boolean;
}

const ERROR_MAPPINGS: ErrorMapping[] = [
  {
    pattern: /No SSO configuration found for domain [`']([^`']+)[`'] or provider [`']([^`']+)[`']/i,
    titleKey: "auth.oauth.errors.noSsoConfigTitle",
    descriptionKey: "auth.oauth.errors.noSsoConfigDesc",
    isAdminError: true,
    extractDomain: true,
    extractProviderId: true,
  },
  {
    pattern: /SSO login failed.*No SSO configuration/i,
    titleKey: "auth.oauth.errors.noSsoConfigTitle",
    descriptionKey: "auth.oauth.errors.noSsoConfigDesc",
    isAdminError: true,
  },
  {
    pattern: /AADSTS700016/i,
    titleKey: "auth.oauth.errors.aadsts700016Title",
    descriptionKey: "auth.oauth.errors.aadsts700016Desc",
    isAdminError: true,
  },
  {
    pattern: /AADSTS50011/i,
    titleKey: "auth.oauth.errors.aadsts50011Title",
    descriptionKey: "auth.oauth.errors.aadsts50011Desc",
    isAdminError: true,
  },
  {
    pattern: /AADSTS65001/i,
    titleKey: "auth.oauth.errors.aadsts65001Title",
    descriptionKey: "auth.oauth.errors.aadsts65001Desc",
    isAdminError: true,
  },
  {
    pattern: /AADSTS90002/i,
    titleKey: "auth.oauth.errors.aadsts90002Title",
    descriptionKey: "auth.oauth.errors.aadsts90002Desc",
    isAdminError: true,
  },
  {
    pattern: /invalid_request.*code verifier/i,
    titleKey: "auth.oauth.errors.codeVerifierTitle",
    descriptionKey: "auth.oauth.errors.codeVerifierDesc",
    isAdminError: false,
  },
  {
    pattern: /code.*expired|expired.*code/i,
    titleKey: "auth.oauth.tokenExpiredTitle",
    descriptionKey: "auth.oauth.tokenExpiredDesc",
    isAdminError: false,
  },
  {
    pattern: /already.*used|used.*already/i,
    titleKey: "auth.oauth.errors.codeAlreadyUsedTitle",
    descriptionKey: "auth.oauth.tokenAlreadyUsedDesc",
    isAdminError: false,
  },
  {
    pattern: /unexpected.*failure|unexpected_failure/i,
    titleKey: "auth.oauth.errors.unexpectedFailureTitle",
    descriptionKey: "auth.oauth.errors.unexpectedFailureDesc",
    isAdminError: true,
  },
  {
    pattern: /database.*error|trigger.*error/i,
    titleKey: "auth.oauth.errors.databaseErrorTitle",
    descriptionKey: "auth.oauth.errors.databaseErrorDesc",
    isAdminError: true,
  },
];

/**
 * Parse an error message and return user-friendly information
 */
function parseErrorMessage(
  message: string,
  t: (key: string, options?: Record<string, string>) => string,
): Partial<CallbackError> {
  for (const mapping of ERROR_MAPPINGS) {
    const match = message.match(mapping.pattern);
    if (match) {
      const result: Partial<CallbackError> = {
        title: t(mapping.titleKey),
        description: t(mapping.descriptionKey),
        isAdminError: mapping.isAdminError,
      };

      if (mapping.extractDomain && match[1]) {
        result.domain = match[1];
        result.description = t(mapping.descriptionKey, { domain: match[1] });
      }

      if (mapping.extractProviderId && match[2]) {
        result.providerId = match[2];
      }

      return result;
    }
  }

  return {};
}

/** Presentational error state for the callback (extracted to keep AuthCallback lean). */
function AuthCallbackErrorState({
  error,
  showDetails,
  copied,
  onToggleDetails,
  onCopyError,
  onBackToLogin,
}: {
  error: CallbackError;
  showDetails: boolean;
  copied: boolean;
  onToggleDetails: () => void;
  onCopyError: () => void;
  onBackToLogin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("common.error")}</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>{error.description}</p>
          {error.domain && (
            <p className="text-xs font-mono bg-destructive/10 px-2 py-1 rounded">
              Domain: {error.domain}
            </p>
          )}
          {error.providerId && (
            <p className="text-xs font-mono bg-destructive/10 px-2 py-1 rounded">
              Provider: {error.providerId}
            </p>
          )}
        </AlertDescription>
      </Alert>

      {/* Admin hint with link to SSO settings */}
      {error.isAdminError && (
        <Alert className="border-outline-variant bg-surface-container-low">
          <Settings className="h-4 w-4 text-primary" />
          <AlertTitle className="text-primary">{t("auth.oauth.errors.adminHintTitle")}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="text-sm">{t("auth.oauth.errors.adminHintDesc")}</p>
            <Link
              to="/tenant/sso"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              <Settings className="h-3 w-3" />
              {t("auth.oauth.errors.openSsoSettings")}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {error.details && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-between"
              onClick={onToggleDetails}
            >
              <span>{t("common.details")}</span>
              {showDetails ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCopyError}
              title={t("auth.oauth.errors.copyError")}
            >
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          {showDetails && (
            <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto max-h-40 overflow-y-auto">
              {error.details}
            </pre>
          )}
        </div>
      )}

      <Button variant="outline" className="w-full" onClick={onBackToLogin}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t("auth.oauth.backToLogin")}
      </Button>
    </>
  );
}

/**
 * Resolve the post-login redirect target. If an originalRedirect is present
 * (Teams deeplinks), extract only its path/search/hash to prevent open-redirect
 * attacks; otherwise fall back to the app root.
 */
function resolveRedirectTarget(originalRedirect: string | null): string {
  if (!originalRedirect) {
    return "/";
  }
  try {
    const url = new URL(originalRedirect);
    // Security: only the path portion — works for same- and cross-origin URLs.
    return url.pathname + url.search + url.hash;
  } catch {
    // Already a relative path? Use it directly; otherwise fall back to root.
    return originalRedirect.startsWith("/") ? originalRedirect : "/";
  }
}

/**
 * Parse OAuth callback parameters from both query string and hash fragment.
 * Some providers return params in hash (#), others in query (?).
 */
function parseCallbackParams(location: { search: string; hash: string }) {
  const queryParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));

  return {
    code: queryParams.get("code") ?? hashParams.get("code"),
    error: queryParams.get("error") ?? hashParams.get("error"),
    errorDescription: queryParams.get("error_description") ?? hashParams.get("error_description"),
  };
}

export default function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<CallbackState>("loading");
  const [error, setError] = useState<CallbackError | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  // Extract original redirect destination (preserved through SSO flow for Teams deeplinks etc.)
  const originalRedirect = searchParams.get("originalRedirect");

  // Ensure callback is processed only once per mount
  const processedRef = useRef(false);

  // Refs for timeout cleanup to prevent navigation after unmount
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect target after successful SSO login (open-redirect-safe).
  const getRedirectTarget = (): string => resolveRedirectTarget(originalRedirect);

  // Helper to schedule navigation with proper cleanup
  const scheduleNavigate = (path: string) => {
    if (navigateTimeoutRef.current) {
      clearTimeout(navigateTimeoutRef.current);
    }
    navigateTimeoutRef.current = setTimeout(() => {
      navigate(path, { replace: true });
    }, 100);
  };

  const handleCopyError = async () => {
    if (!error) return;

    const errorInfo = [
      `Error: ${error.title}`,
      `Description: ${error.description}`,
      error.domain ? `Domain: ${error.domain}` : null,
      error.providerId ? `Provider ID: ${error.providerId}` : null,
      error.details ? `\nDetails:\n${error.details}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await navigator.clipboard.writeText(errorInfo);
      setCopied(true);
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      logger.debug("Clipboard copy failed", errorInfo);
    }
  };

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    // Fresh callback handling: reset the in-memory digest accumulator so the
    // persisted set is rebuilt only from fingerprints computed here.
    resetProcessedCodes();

    const handleCallback = async () => {
      const { code, error: errorParam, errorDescription } = parseCallbackParams(location);

      // Check for error from provider
      if (errorParam) {
        const fullMessage = `${errorParam}: ${errorDescription || ""}`;
        const parsed = parseErrorMessage(fullMessage, t);

        // Track provider callback error in Sentry
        trackSsoCallbackError(fullMessage, {
          domain: parsed.domain,
          providerId: parsed.providerId,
          isAdminError: parsed.isAdminError,
          extra: {
            errorParam,
            errorDescription,
            azureErrorCode: extractAzureErrorCode(fullMessage),
          },
        });

        setState("error");
        setError({
          title: parsed.title || t("auth.oauth.callbackErrorTitle"),
          description: parsed.description || errorDescription || errorParam,
          details: `Error: ${errorParam}${errorDescription ? `\nDescription: ${errorDescription}` : ""}`,
          isAdminError: parsed.isAdminError || false,
          domain: parsed.domain,
          providerId: parsed.providerId,
        });
        return;
      }

      // If no code present, retry session check multiple times (handles slow propagation)
      if (!code) {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const {
              data: { session },
            } = await supabase.auth.getSession();
            if (session) {
              logger.debug(`Session found on retry attempt ${attempt + 1}`);
              setState("success");
              scheduleNavigate(getRedirectTarget());
              return;
            }
          } catch (err) {
            logger.error("Session check error:", err);
          }
          // Wait 500ms before next attempt (except on last attempt)
          if (attempt < 2) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        // After all retries failed, show error
        setState("error");
        setError({
          title: t("auth.oauth.callbackMissingCodeTitle"),
          description: t("auth.oauth.callbackMissingCodeDesc"),
        });
        return;
      }

      // Check if this code was already processed (prevents double-processing on mobile)
      if (await isCodeAlreadyProcessed(code)) {
        logger.debug("OAuth code already processed, checking for existing session...");
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            setState("success");
            scheduleNavigate(getRedirectTarget());
            return;
          }
        } catch (err) {
          logger.error("Session check error:", err);
        }

        // Code was processed but no session - redirect to auth
        navigate("/auth", { replace: true });
        return;
      }

      // Mark code as processed before attempting exchange
      await markCodeAsProcessed(code);

      try {
        // Pre-exchange session check: if session already exists, skip code exchange
        // This prevents race-condition errors when the session was created in another tab/reload
        const {
          data: { session: preExistingSession },
        } = await supabase.auth.getSession();
        if (preExistingSession) {
          logger.debug("Session already exists before code exchange, redirecting...");
          setState("success");
          scheduleNavigate(getRedirectTarget());
          return;
        }

        // Exchange the code for a session
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

        if (exchangeError) {
          logger.error("Code exchange error:", exchangeError);

          // Check if user already has a valid session (code might have been processed in another tab/reload)
          const {
            data: { session: existingSession },
          } = await supabase.auth.getSession();
          if (existingSession) {
            logger.debug("User already has a valid session, redirecting...");
            setState("success");
            scheduleNavigate(getRedirectTarget());
            return;
          }

          // Parse the error for user-friendly message
          const errorMessage = exchangeError.message || JSON.stringify(exchangeError);
          const parsed = parseErrorMessage(errorMessage, t);

          // Track code exchange error in Sentry
          trackSsoCodeExchangeError(errorMessage, {
            isAdminError: parsed.isAdminError,
            extra: {
              errorCode: exchangeError.code,
              status: exchangeError.status,
              azureErrorCode: extractAzureErrorCode(errorMessage),
              domain: parsed.domain,
              providerId: parsed.providerId,
            },
          });

          setState("error");
          setError({
            title: parsed.title || t("auth.oauth.callbackErrorTitle"),
            description: parsed.description || exchangeError.message,
            details: JSON.stringify(exchangeError, null, 2),
            isAdminError: parsed.isAdminError || false,
            domain: parsed.domain,
            providerId: parsed.providerId,
          });
          return;
        }

        if (data.session) {
          setState("success");
          // Small delay to ensure state is updated before navigation
          scheduleNavigate(getRedirectTarget());
        } else {
          setState("error");
          setError({
            title: t("auth.oauth.callbackErrorTitle"),
            description: t("auth.oauth.callbackNoSessionDesc"),
          });
        }
      } catch (err) {
        logger.error("Callback error:", err);

        // Check for existing session as fallback
        try {
          const {
            data: { session: existingSession },
          } = await supabase.auth.getSession();
          if (existingSession) {
            logger.debug("User already has a valid session after error, redirecting...");
            setState("success");
            scheduleNavigate(getRedirectTarget());
            return;
          }
        } catch {
          // Ignore session check errors
        }

        const errorMessage = err instanceof Error ? err.message : String(err);
        const parsed = parseErrorMessage(errorMessage, t);

        // Track unexpected callback error in Sentry
        trackSsoCallbackError(errorMessage, {
          domain: parsed.domain,
          providerId: parsed.providerId,
          isAdminError: parsed.isAdminError,
          extra: {
            errorType: "exception",
            stack: err instanceof Error ? err.stack : undefined,
          },
        });

        setState("error");
        setError({
          title: parsed.title || t("auth.oauth.callbackErrorTitle"),
          description: parsed.description || (err instanceof Error ? err.message : "Unknown error"),
          details: err instanceof Error ? err.stack : String(err),
          isAdminError: parsed.isAdminError || false,
          domain: parsed.domain,
          providerId: parsed.providerId,
        });
      }
    };

    handleCallback();

    // Cleanup timeouts on unmount to prevent navigation after component is gone
    return () => {
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current);
      }
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, t, location]);

  const handleBackToLogin = () => {
    navigate("/auth", { replace: true });
  };

  return (
    <div
      className="min-h-[100dvh] flex items-center justify-center bg-surface p-4"
      data-testid="auth-shell"
    >
      <div className="w-full max-w-md animate-fade-in">
        <AuthBrandMark appName={t("auth.appName")} className="mb-8" />

        <Card
          className="border-outline-variant bg-surface-container-lowest shadow-none"
          data-testid="auth-card"
        >
          <CardHeader className="text-center pb-4">
            {state === "loading" && (
              <>
                <CardTitle className="text-xl">{t("auth.oauth.finishingLoginTitle")}</CardTitle>
                <CardDescription>{t("auth.oauth.finishingLoginDesc")}</CardDescription>
              </>
            )}
            {state === "error" && error && (
              <>
                <CardTitle className="text-xl text-destructive">{error.title}</CardTitle>
                <CardDescription>{error.description}</CardDescription>
              </>
            )}
            {state === "success" && (
              <>
                <CardTitle className="text-xl text-success">
                  {t("auth.oauth.loginSuccessTitle")}
                </CardTitle>
                <CardDescription>{t("auth.oauth.loginSuccessDesc")}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {state === "loading" && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
                <p className="text-sm text-muted-foreground">{t("auth.oauth.processingLogin")}</p>
              </div>
            )}

            {state === "error" && error && (
              <AuthCallbackErrorState
                error={error}
                showDetails={showDetails}
                copied={copied}
                onToggleDetails={() => setShowDetails(!showDetails)}
                onCopyError={handleCopyError}
                onBackToLogin={handleBackToLogin}
              />
            )}

            {state === "success" && (
              <div className="flex flex-col items-center justify-center py-4">
                <Loader2 className="h-8 w-8 text-success animate-spin" />
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">{t("auth.appTagline")}</p>
      </div>
    </div>
  );
}
